import { roundDecimalPlaces, distancePt, determineCurveBend, getCenterPt, preDetermineCurveBend } from './utils.js'

// This class deals with filling/expanding shapes 
// based on their parameters and some arguments. 
// Will probably be most aimed at circles but can see what else it might 
// work for
class ShapeFillSeries {
    constructor(
        shape
    ) {
        // shape needs a getCoordinates() method for this to work.  
        // and getSubCurveRelative() method
        this.shape = shape
    }

    getSubSizes(args) {
        let completeSize = args.coverage / args.curveCount
        if (args.subSizes.length === 0) {
            return Array(args.curveCount).fill(completeSize)
        } else {
            let normalize = args.subSizes.reduce((a, b) => a + b)
            let subs = args.subSizes.map(a => (a / normalize) * completeSize)
            return Array(args.curveCount).fill(subs).flat()
        }

    }

    getSwirlOffset(args, i) {
        return args.rotation * i + args.startParam
    }

    getPadLength(args, curveCoverage) {
        let padLengths = []
        let blankSize = 1 - curveCoverage
        if (args.subSizes.length > 1) {
            for (let i = 0; i < args.curveCount; i++) {
                for (let sub of args.subSizes) {
                    padLengths.push(blankSize / args.curveCount * sub)
                }
            }
        } else {
            padLengths = Array(args.curveCount).fill(blankSize / args.curveCount)
        }
        return padLengths
    }

    // Based on the inputs, calculate the center points (1 dim arr)
    // for all of the curves, and then all the curve points moving outward 
    // in a 2-dim array, with pairs in that array representing the 
    // curves endpoints in an outward progression.  
    getDistanceParams(args) {
        let centerParams = []
        let endParams = []
        let padLengths = this.getPadLength(args, args.coverage)
        let allCurveSizes = this.getSubSizes(args)
        let originalCurveSizes = allCurveSizes.slice(0)
        for (let i = 0; i < args.maxLayers; i++) {
            let startOffset = this.getSwirlOffset(args, i)
            let sizeFactor = args.sizeFactors[i % args.sizeFactors.length]
            centerParams.push([])
            endParams.push([])
            let prevParam = startOffset
            let n = 0
            for (let size of allCurveSizes) {
                size = size
                let start = (padLengths[n] / 2) + prevParam
                let center = (size * sizeFactor / 2) + start
                centerParams.at(-1).push(roundDecimalPlaces(center, 5))
                endParams.at(-1).push(roundDecimalPlaces(start, 5))
                endParams.at(-1).push(roundDecimalPlaces(start + size * sizeFactor, 5))
                prevParam = start + size + (padLengths[n] / 2)
                n++
            }
            if (args.changeCompound) {
                allCurveSizes = allCurveSizes.map(size => size * (1 + args.change))
            } else {
                allCurveSizes = originalCurveSizes.map(size => size * (1 + args.change * (i + 1)))
            }
            padLengths = this.getPadLength(args, allCurveSizes.reduce((a, b) => a + b))
        }
        return [centerParams, endParams]
    }


    // The curve points are a 3 dim array to account for being 
    // different for each input shape, while the params will be the same 
    // for each of them
    getCurvePoints(endsParams, args) {
        let curvePoints = []
        let distance = 1
        for (let params of endsParams) {
            curvePoints.push([])
            for (let i = 0; i < params.length - 1; i+=2) {
                curvePoints.at(-1).push(distancePt(this.shape, args.centerPt, params.at(i), distance))
                curvePoints.at(-1).push(distancePt(this.shape, args.centerPt, params.at(i + 1), distance))
                curvePoints.at(-1).push(distancePt(this.shape, args.centerPt, params.at(i + 1), distance + args.thickness))
                curvePoints.at(-1).push(distancePt(this.shape, args.centerPt, params.at(i), distance + args.thickness))
            }
            distance += args.thickness + args.spacing
        }
        return curvePoints
    }

    // TODO: If I can get true 'smooth' using a bezier, that would
    //       be neat. Likely need to sort out how to draw line at angle 
    //       from line regardless of orientation however.
    buildCurve(pointArr, paramsIn, paramsOut, sizeArr, connectPoints) {
        let path = ``
        let [startInnerPt, endInnerPt, startOuterPt, endOuterPt] = pointArr
        let [sizeInner, sizeOuter] = sizeArr

        // Go to start
        path = `${path} M ${startInnerPt.x} ${startInnerPt.y} `

        // draw to inner arc end
        path = connectPoints ? `${path} L ${endInnerPt.x} ${endInnerPt.y} ` : `${path} ${this.shape.getSubCurveRelative(...paramsIn, sizeInner)} `

        // line to outer arc start
        path = `${path} L ${startOuterPt.x} ${startOuterPt.y} `

        // draw outer arc
        path = connectPoints ? `${path} L ${endOuterPt.x} ${endOuterPt.y} ` : `${path} ${this.shape.getSubCurveRelative(...paramsOut.reverse(), sizeOuter)} `

        // line back to start point
        path = `${path} L ${startInnerPt.x} ${startInnerPt.y} `
        return path
    }


    getLayerShapes(curvePoints, endParams, args) {
        let shapePaths = []
        let n = 1
        for (let layerPoints of curvePoints) {
            let layerParams = endParams.at(n - 1)
            let sizeInner = 1 + ((n - 1) * (args.thickness + args.spacing))
            let sizeOuter = 1 + (n * (args.thickness) + (n - 1) * args.spacing)
            for (let i = 0; i < layerPoints.length - 3; i += 4) {
                let params = layerParams.slice(i / 2, i / 2 + 2)
                let points = layerPoints.slice(i, i + 4)
                shapePaths.push(this.buildCurve(points, params, params, [sizeInner, sizeOuter], false))
            }
            n++
        }
        return shapePaths
    }


    buildSmoothCurve(pointArr, ctrlPoints, paramsIn, paramsOut, sizeArr, connectPoints) {
        let path = ``
        let [startInnerPt, endInnerPt, startOuterPt, endOuterPt] = pointArr
        let [sizeInner, sizeOuter] = sizeArr
        let [ctrlOne, ctrlTwo] = ctrlPoints

        // Go to start
        path = `${path} M ${startInnerPt.x} ${startInnerPt.y} `

        // draw to inner arc end
        path = connectPoints ? `${path} L ${endInnerPt.x} ${endInnerPt.y} ` : `${path} ${this.shape.getSubCurveRelative(...paramsIn, sizeInner)} `

        // draw the bezier from first arc end to second arc start
        path = `${path} Q ${ctrlOne.x} ${ctrlOne.y} ${startOuterPt.x} ${startOuterPt.y} `
        // path = `${path} L ${startOuterPt.x} ${startOuterPt.y} `

        // draw outer arc
        path = connectPoints ? `${path} L ${endOuterPt.x} ${endOuterPt.y} ` : `${path} ${this.shape.getSubCurveRelative(...paramsOut.reverse(), sizeOuter)} `

        // line back to start point
        path = `${path} Q ${ctrlTwo.x} ${ctrlTwo.y} ${startInnerPt.x} ${startInnerPt.y} `
        // path = `${path} L ${startInnerPt.x} ${startInnerPt.y} `
        return path
    }


    continueSmoothCurve(pointArr, ctrlPoints, paramsIn, paramsOut, sizeArr, connectPoints) {
        let path = ``
        let [startInnerPt, endInnerPt, startOuterPt, endOuterPt] = pointArr
        let [sizeInner, sizeOuter] = sizeArr
        let [ctrlOne, ctrlTwo] = ctrlPoints

        // Go to start
        path = `${path} M ${startInnerPt.x} ${startInnerPt.y} `

        // draw to inner arc end
        path = connectPoints ? `${path} L ${endInnerPt.x} ${endInnerPt.y} ` : `${path} ${this.shape.getSubCurveRelative(...paramsIn, sizeInner)} `

        // draw the bezier from first arc end to second arc start
        path = `${path} T ${startOuterPt.x} ${startOuterPt.y} `
        // path = `${path} L ${startOuterPt.x} ${startOuterPt.y} `

        // draw outer arc
        path = connectPoints ? `${path} L ${endOuterPt.x} ${endOuterPt.y} ` : `${path} ${this.shape.getSubCurveRelative(...paramsOut.reverse(), sizeOuter)} `

        // line back to start point
        path = `${path} T ${startInnerPt.x} ${startInnerPt.y} `
        // path = `${path} L ${startInnerPt.x} ${startInnerPt.y} `
        return path
    }


    // Need to always pair inner/inner, 
    // and then skip the last outer since that would lead to one awkward 
    // square layer on top.
    getSmoothShapes(curvePoints, endParams, args) {
        let shapePaths = []
        let n = 1
        let debugPoints = []
        let preDeterminePrev = []
        for (let i = 0; i < curvePoints.length - 1; i++) {
            let currentPoints = curvePoints[i]
            let nextPoints = curvePoints[i + 1]
            let beyondPoints = []
            let preDetermine = []
            if (i + 2 < curvePoints.length - 1) {
                beyondPoints = curvePoints[i + 2]
            }
            let currentParams = endParams[i]
            let nextParams = endParams[i + 1]
            let sizeInner = 1 + ((n - 1) * (args.thickness + args.spacing))
            let sizeOuter = 1 + (n * (args.thickness) + (n - 1) * args.spacing)
            for (let k = 0; k < currentPoints.length - 3; k += 4) {
                let paramsIn = currentParams.slice(k / 2, k / 2 + 2)
                let paramsOut = nextParams.slice(k / 2, k / 2 + 2)
                let ctrlPoints = []
                let points = currentPoints.slice(k, k + 2)
                let nexts = nextPoints.slice(k, k + 2).reverse()

                // Get the control points. 
                // Uses the current, next, and one after that. 
                // If there are not enough left to use, memoized the 
                // last curve direction and uses that.
                if (beyondPoints.length > 0) {
                    let beyonds = beyondPoints.slice(k, k + 2).reverse()
                    let [ctrlPt1, debugPts1, dir1, fac1] = determineCurveBend(points[1], nexts[0], beyonds[0])

                    ctrlPoints.push(ctrlPt1)
                    preDetermine.push(dir1, fac1)
                    let [ctrlPt2, debugPts2, dir2, fac2] = determineCurveBend(points[0], nexts[1], beyonds[1])
                    ctrlPoints.push(ctrlPt2)
                    preDetermine.push(dir2, fac2)
                    debugPoints = debugPoints.concat(debugPts1)
                    debugPoints = debugPoints.concat(debugPts2)
                } else {
                    ctrlPoints.push(preDetermineCurveBend(points[1], nexts[0], preDeterminePrev[k], preDeterminePrev[k + 1])[0])
                    ctrlPoints.push(preDetermineCurveBend(points[0], nexts[1], preDeterminePrev[k + 2], preDeterminePrev[k + 3])[0])
                }
                points.push(...nexts)
                shapePaths.push(this.buildSmoothCurve(points, ctrlPoints, paramsIn, paramsOut, [sizeInner, sizeOuter], false))
            }
            if (preDetermine.length > 0) {
                preDeterminePrev = preDetermine
            }
            n++
        }
        return [shapePaths, debugPoints]
    }

    //// Primary Method. 
    // Call this with a ShapeFillSeriesArgs object to get the 
    // desired filling shapes
    fillShape(args) {
        let [centerParams, endParams] = this.getDistanceParams(args)
        let curvePoints = this.getCurvePoints(endParams, args)
        if (args.smooth) {
            let [getLayerShapes, debugPoints] = this.getSmoothShapes(curvePoints, endParams, args)
            return [getLayerShapes, debugPoints]
        } else {

            let getLayerShapes = this.getLayerShapes(curvePoints, endParams, args)
            return [getLayerShapes, curvePoints]
        }
    }

}

export { ShapeFillSeries }
